import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { WizardStage, ProjectUsageResponse } from '@historyjki/shared';
import { AppHeader } from './AppHeader';
import { WIZARD_STAGES, WIZARD_STAGE_LABELS, WIZARD_STAGE_DESCRIPTIONS } from '@historyjki/shared';
import { InlineEdit } from './ui/InlineEdit';
import { CaptureStage } from './stages/CaptureStage';
import { DefineStage } from './stages/DefineStage';
import { ComposeStage } from './stages/ComposeStage';
import { ExportStage } from './stages/ExportStage';
import { AiUsageModal } from './AiUsageModal';
import { api } from '../utils/api';
import type { useProject } from '../hooks/useProject';

type ProjectHookReturn = ReturnType<typeof useProject>;

interface WizardLayoutProps {
  projectHook: ProjectHookReturn;
}

const STAGE_INDEX: Record<string, number> = {};
WIZARD_STAGES.forEach((s, i) => {
  STAGE_INDEX[s] = i;
});

export function WizardLayout({ projectHook }: WizardLayoutProps) {
  const { project, updateProject } = projectHook;
  const navigate = useNavigate();

  const [viewStage, setViewStage] = useState<WizardStage>(project?.stage ?? 'capture');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [usage, setUsage] = useState<ProjectUsageResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);

  const wrappedUpdateProject: typeof updateProject = useCallback(
    async (data) => {
      if (data.stage) {
        const targetIndex = STAGE_INDEX[data.stage] ?? 0;
        const maxIndex = STAGE_INDEX[project?.stage ?? 'capture'] ?? 0;

        if (targetIndex <= maxIndex) {
          setViewStage(data.stage);
          const { stage: _stage, ...rest } = data;
          if (Object.keys(rest).length > 0) {
            const ok = await updateProject(rest);
            if (ok) setLastSaved(new Date());
            return ok;
          }
          return true;
        }

        setViewStage(data.stage);
        const ok = await updateProject(data);
        if (ok) setLastSaved(new Date());
        return ok;
      }

      const ok = await updateProject(data);
      if (ok) setLastSaved(new Date());
      return ok;
    },
    [updateProject, project?.stage],
  );

  const wrappedProjectHook = useMemo(
    () => ({
      ...projectHook,
      updateProject: wrappedUpdateProject,
    }),
    [projectHook, wrappedUpdateProject],
  );

  useEffect(() => {
    const id = project?.id;
    if (!id) return;
    let active = true;
    const fetchUsage = (withLoading: boolean) => {
      if (withLoading) setUsageLoading(true);
      api
        .get<ProjectUsageResponse>(`/projects/${id}/usage`)
        .then((data) => {
          if (active) setUsage(data);
        })
        .catch(() => {})
        .finally(() => {
          if (active && withLoading) setUsageLoading(false);
        });
    };
    fetchUsage(true); // immediate on mount
    // Backend logs AI cost to ai_usage_log after each operation, but the
    // frontend has no signal when that happens. Poll so the badge reflects
    // any AI activity within the interval while the wizard is open.
    const interval = setInterval(() => fetchUsage(false), 30_000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [project?.id]);

  if (!project) return null;

  const maxIndex = STAGE_INDEX[project.stage] ?? 0;
  const viewIndex = STAGE_INDEX[viewStage] ?? 0;

  // The Compose stage uses a full-viewport "focus" layout: a fixed canvas
  // workspace with its own internal panels. It reuses the wizard sidebar
  // but turns the main column into a flex column (see the .focus-wiz /
  // .focus-main rules in wizard.css).
  const isFocus = viewStage === 'compose';

  const handleStageClick = (stage: WizardStage) => {
    const targetIndex = STAGE_INDEX[stage];
    if (targetIndex <= maxIndex) {
      setViewStage(stage);
    }
  };

  const handleTitleSave = (newTitle: string) => {
    void wrappedUpdateProject({ title: newTitle });
  };

  const formatSaveTime = (date: Date) =>
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const renderStage = () => {
    switch (viewStage) {
      case 'capture':
        return <CaptureStage projectHook={wrappedProjectHook} />;
      case 'define':
        return <DefineStage projectHook={wrappedProjectHook} />;
      case 'compose':
        return <ComposeStage projectHook={wrappedProjectHook} />;
      case 'export':
        return <ExportStage projectHook={wrappedProjectHook} />;
      default:
        return <CaptureStage projectHook={wrappedProjectHook} />;
    }
  };

  return (
    <>
      <AppHeader />
      <div className={`wiz${isFocus ? ' focus-wiz' : ''}`}>
      <aside className="wiz__side">
        <div>
          <button className="wiz__back" onClick={() => navigate('/')} type="button">
            <span>&#8592;</span>
            <span>Stories</span>
          </button>
          <InlineEdit
            value={project.title}
            onSave={handleTitleSave}
            tag="h2"
            className="wiz__title"
            placeholder="Untitled Story"
          />
        </div>

        <nav className="toc">
          <div className="toc__rule" />
          {WIZARD_STAGES.map((stage, index) => {
            const isDone = index < maxIndex;
            const isCurrent = index === viewIndex;
            const isLocked = index > maxIndex;

            return (
              <button
                key={stage}
                className={`toc__item${isDone ? ' toc__item--done' : ''}${isCurrent ? ' toc__item--current' : ''}${isLocked ? ' toc__item--locked' : ''}`}
                onClick={() => handleStageClick(stage as WizardStage)}
                disabled={isLocked}
                type="button"
              >
                <div className="toc__node">
                  {isDone ? <span>&#10003;</span> : <span>{index + 1}</span>}
                </div>
                <div>
                  <div className="toc__label">{WIZARD_STAGE_LABELS[stage]}</div>
                  <div className="toc__desc">{WIZARD_STAGE_DESCRIPTIONS[stage]}</div>
                </div>
              </button>
            );
          })}
        </nav>

        <div className="wiz__autosave">
          <span className="wiz__autosave-dot" />
          {lastSaved ? `AUTOSAVED · ${formatSaveTime(lastSaved)}` : 'AUTOSAVED'}
          {usageLoading ? (
            <span className="wiz__cost">···</span>
          ) : (
            <button
              className="wiz__cost wiz__cost--btn"
              onClick={() => setUsageOpen(true)}
              type="button"
              title="View AI usage"
            >
              {usage ? `$${usage.total_usd < 0.01 && usage.total_usd > 0 ? usage.total_usd.toFixed(4) : usage.total_usd.toFixed(2)}` : '$0.00'}
            </button>
          )}
        </div>
      </aside>

      <AiUsageModal open={usageOpen} onClose={() => setUsageOpen(false)} usage={usage} />

      <main className={`wiz__main${isFocus ? ' focus-main' : ''}`}>
        {renderStage()}
      </main>
    </div>
    </>
  );
}
